import { useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CategoryCard, CategoryForm, useCategories, usePopularCategories, useCategoriesWithoutSections, categoriesApi } from '@modules/categories';
import { useSections } from '@modules/sections';
import { Breadcrumbs, type BreadcrumbItem } from '@components';
import { Button, Modal, RefetchButton } from '@shared/ui';
import '@shared/styles/page.css';
import './SectionCategoriesPage.css';
import { IoAdd } from 'react-icons/io5';

function readAdminRole(): 'SUPER_ADMIN' | 'SELLER_ADMIN' | null {
  try {
    const raw = localStorage.getItem('luxus_admin');
    if (!raw) return null;
    const p = JSON.parse(raw) as { role?: string };
    return p.role === 'SELLER_ADMIN' ? 'SELLER_ADMIN' : p.role === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : null;
  } catch {
    return null;
  }
}

export default function SectionCategoriesPage({ embedded = false }: { embedded?: boolean }) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [isSyncCronLoading, setIsSyncCronLoading] = useState(false);
  const { sectionId } = useParams<{ sectionId?: string }>();
  const location = useLocation();
  const isPopular = location.pathname === '/sections/popular' || sectionId === 'popular';
  const isWithoutSections = location.pathname === '/sections/without-sections' || sectionId === 'without-sections';
  const sectionIdNum = sectionId && !isPopular && !isWithoutSections ? parseInt(sectionId, 10) : undefined;

  const categoriesQuery = isPopular
    ? usePopularCategories()
    : isWithoutSections
      ? useCategoriesWithoutSections()
      : useCategories(sectionIdNum);

  const { data: categories = [], isLoading: categoriesLoading, error, refetch } = categoriesQuery;
  const { data: sections = [] } = useSections();
  const section = !isPopular ? sections.find(s => s.id === sectionIdNum) : null;
  const isSuperAdmin = useMemo(() => readAdminRole() === 'SUPER_ADMIN', [location.pathname]);

  const handleRunSyncPopularCron = async () => {
    setIsSyncCronLoading(true);
    try {
      const result = await categoriesApi.runSyncPopularCron();
      await refetch();
      notifications.show({
        title: 'Готово',
        message: `Крон записал места: ${result.marked} шт. [${(result.ranks ?? []).map((r) => `${r.id}→${r.popularRank}`).join(', ')}]`,
        color: 'green',
      });
    } catch (error) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      notifications.show({
        title: 'Ошибка',
        message: msg ?? 'Не удалось выполнить крон (нужны права супер-админа)',
        color: 'red',
      });
    } finally {
      setIsSyncCronLoading(false);
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Главная', path: '/' },
    { label: 'Категории', path: '/sections' },
  ];

  if (isPopular) {
    breadcrumbs.push({ label: 'Популярные' });
  } else if (isWithoutSections) {
    breadcrumbs.push({ label: 'Без секции' });
  } else if (section) {
    breadcrumbs.push({ label: section.name });
  }

  const getContent = () => {
    if (categoriesLoading) {
      return (
        <div className="sections-categories-status">
          <Loader />
        </div>
      );
    }

    if (error && refetch) {
      return (
        <RefetchButton
          refetch={refetch}
          isLoading={categoriesLoading}
        />
      );
    }

    const canAdd = !isPopular && !isWithoutSections && Boolean(sectionId);

    return (
      <div className="sections-categories-grid">
        {categories.length === 0 ? (
          <p className="sections-categories-empty">
            {isPopular ? 'Нет популярных категорий' : isWithoutSections ? 'Нет категорий без секций' : 'Категорий пока нет'}
          </p>
        ) : (
          categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={() => setEditingCategoryId(category.id)}
              sectionId={isPopular ? 'popular' : isWithoutSections ? undefined : sectionIdNum}
              hideMenu={isPopular}
            />
          ))
        )}
        {canAdd ? (
          <button
            type="button"
            className="sections-category-add"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <IoAdd size={18} aria-hidden />
            <span>Категория</span>
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className={embedded ? 'sections-categories-panel' : 'page-container'}>
      {embedded ? null : <Breadcrumbs items={breadcrumbs} />}

      {isPopular && isSuperAdmin ? (
        <div className="sections-categories-panel__cron">
          <Button
            size="xs"
            variant="secondary"
            onClick={handleRunSyncPopularCron}
            disabled={isSyncCronLoading}
            loading={isSyncCronLoading}
          >
            Запустить крон
          </Button>
        </div>
      ) : null}

      {getContent()}

      {!isPopular && !isWithoutSections && sectionIdNum && (
        <Modal
          isOpen={isCreateModalOpen || editingCategoryId !== null}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingCategoryId(null);
          }}
          title={editingCategoryId ? "Редактировать категорию" : "Создать категорию"}
        >
          <CategoryForm
            category={editingCategoryId ? categories.find(c => c.id === editingCategoryId) : undefined}
            sectionId={sectionIdNum}
            onSuccess={() => {
              setIsCreateModalOpen(false);
              setEditingCategoryId(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
